import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Play } from "lucide-react";
import { siteData } from "@/lib/aht/pageData";
import { Img } from "@/components/aht/Img";
import { V, fillJsx } from "@/components/aht/Verify";
import { CountUp } from "@/components/aht/CountUp";
import { VideoModal } from "@/components/aht/Overlays";
import { noOrphan } from "@/lib/aht/format";
import { EASE, scrollToId } from "@/lib/aht/hooks";

const decimalsOf = (n) => (Number.isInteger(n) ? 0 : String(n).split(".")[1].length);

// The hero photo is in .wrap-wide (1440px max), so phones take the small source and desktops the large one.
export const HERO_SIZES = "(min-width: 1500px) 1440px, 100vw";
const PHOTO_TRANSITION = { duration: 0.8, delay: 0.45, ease: EASE };

export default function Hero() {
  const { subject, meta, agent, ui } = siteData;
  const h = ui.hero;
  const photo = subject.heroPhoto;
  const largest = photo && [...photo.sources].sort((a, b) => b.width - a.width)[0];
  const reduce = useReducedMotion();
  const [factsStart, setFactsStart] = useState(Boolean(reduce));
  const [video, setVideo] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFactsStart(true), reduce ? 0 : 1200);
    return () => clearTimeout(t);
  }, [reduce]);

  const facts = [
    { key: "beds", value: subject.beds, label: h.facts.beds },
    { key: "baths", value: subject.baths, label: h.facts.baths, decimals: decimalsOf(subject.baths) },
    { key: "sqft", value: subject.sqft, label: h.facts.sqft },
    { key: "lot", value: subject.lotAcres, label: h.facts.lot, decimals: decimalsOf(subject.lotAcres), unit: ui.units.acre },
    { key: "built", value: subject.yearBuilt, label: h.facts.built, plain: true },
  ];

  return (
    <section
      id="top"
      data-stop=""
      data-tone="paper"
      aria-labelledby="top-title"
      className="relative bg-paper"
      style={{ paddingTop: "clamp(48px, 7vw, 104px)", paddingBottom: "var(--section-pad)" }}
      data-testid="section-hero"
    >
      <motion.div
        className="wrap text-center"
        initial={reduce ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
      >
        <p className="t-ui text-graphite" data-testid="hero-prepared-for">
          {fillJsx(h.preparedFor, { name: <V item={meta.preparedFor} /> })}
        </p>
        <h1 id="top-title" className="t-hero text-balance mx-auto mt-5 max-w-[11ch]" data-testid="hero-address">
          {noOrphan(subject.address)}
        </h1>
        <p className="t-tile mt-5 text-balance">{noOrphan(subject.neighborhood)}</p>
        <p className="t-intro text-pretty mx-auto mt-5 max-w-[36ch] text-graphite">{noOrphan(h.tagline)}</p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={agent.calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary w-full sm:w-auto"
            data-testid="hero-book-call-button"
          >
            {h.bookCall}
          </a>
          <button
            type="button"
            onClick={() => scrollToId("your-home", reduce)}
            className="btn btn-secondary w-full sm:w-auto"
            data-testid="hero-see-plan-button"
          >
            {h.seePlan}
          </button>
        </div>
        {agent.videoUrl && (
          <button
            type="button"
            onClick={() => setVideo(true)}
            className="btn btn-sm mt-4 bg-mist text-ink"
            data-testid="hero-watch-video-button"
          >
            <Play size={14} aria-hidden="true" />
            {h.watchVideo}
          </button>
        )}
      </motion.div>

      {photo && (
        <div className="wrap-wide" style={{ marginTop: "clamp(48px, 6vw, 80px)" }}>
          {/* Hero sequence, step 2: the photo settles from 98.5% while a paper-colored veil fades off it.
              Fading a veil out (instead of the photo in) looks identical but lets the photo paint at once,
              so the browser's largest-contentful-paint mark lands when the photo is actually ready. */}
          <motion.div
            className="relative"
            initial={reduce ? false : { scale: 0.985 }}
            animate={{ scale: 1 }}
            transition={PHOTO_TRANSITION}
          >
            <Img
              src={largest.src}
              sources={photo.sources}
              sizes={HERO_SIZES}
              alt={photo.alt}
              priority
              className="aspect-[4/3] sm:aspect-[16/9] md:aspect-[21/9]"
              testId="hero-photo"
            />
            {photo.label && (
              <span
                className="absolute bottom-3 left-3 inline-flex max-w-[calc(100%-24px)] items-center rounded-full bg-ink px-3 py-2 text-[13px] font-medium leading-tight text-paper sm:bottom-5 sm:left-5 sm:px-3.5 sm:text-[14px]"
                data-testid="hero-photo-label"
              >
                {photo.label}
              </span>
            )}
            {!reduce && !revealed && (
              <motion.div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-tile bg-paper"
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={PHOTO_TRANSITION}
                onAnimationComplete={() => setRevealed(true)}
                data-testid="hero-photo-veil"
              />
            )}
          </motion.div>
        </div>
      )}

      <div className="wrap" style={{ marginTop: "clamp(40px, 5vw, 72px)" }}>
        <dl className="flex flex-wrap justify-center gap-y-8 md:flex-nowrap" data-testid="hero-facts">
          {facts.map((f) => (
            <div
              key={f.key}
              className="flex w-1/3 flex-col-reverse items-center text-center md:w-auto md:px-[clamp(20px,3.4vw,56px)]"
            >
              <dt className="t-ui mt-1 text-graphite">{f.label}</dt>
              <dd className="t-stat-sm m-0 whitespace-nowrap" data-testid={`hero-fact-${f.key}`}>
                <CountUp value={f.value} start={factsStart} decimals={f.decimals || 0} plain={f.plain} align="center" />
                {f.unit && <span className="ml-1.5 text-[0.5em] font-semibold tracking-normal text-graphite">{f.unit}</span>}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {agent.videoUrl && (
        <VideoModal open={video} onClose={() => setVideo(false)} url={agent.videoUrl} title={h.videoTitle} closeLabel={h.closeVideo} />
      )}
    </section>
  );
}
